import { VideoCardProps } from 'components/classes/Cards/VideoCard/VideoCard';
import {
  SLUG_DEFAULT,
  makeSlug,
} from 'app/classes/_components/_utils/classHelpers';
import { VideoLesson } from 'types/classes';
import { DocumentType } from 'app/classes/_lib/favorites/documentType.type';
import type { FavoritesMap } from 'app/classes/_lib/favorites/getFavoriteIdsByType';

export const TYPE_KEYS = {
  articleLesson: 'read',
  recipeLesson: '',
  videoLesson: 'video',
} as const;

export type TypeKey = keyof typeof TYPE_KEYS;

export const getTypeString = (documentType?: DocumentType): string => {
  if (documentType && documentType in TYPE_KEYS) {
    return TYPE_KEYS[documentType as TypeKey];
  }
  return 'read';
};

export type DurationFormat = 'time' | 'readable';

export const formatDuration = (
  duration?: string,
  options?: {
    format?: DurationFormat;
    documentType?: DocumentType;
  },
): string | undefined => {
  if (!duration) return undefined;

  const regex = /P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
  const matches = duration.toLocaleUpperCase().match(regex);

  if (!matches) return undefined;

  let days = matches[1] ? parseInt(matches[1], 10) : 0;
  let hours = matches[2] ? parseInt(matches[2], 10) : 0;
  const minutes = matches[3] ? parseInt(matches[3], 10) : 0;
  const seconds = matches[4] ? parseInt(matches[4], 10) : 0;

  if (hours >= 24) {
    days += Math.floor(hours / 24);
    hours = hours % 24;
  }

  const format = options?.format ?? 'time';
  const documentType = options?.documentType;

  if (format === 'time') {
    const formattedSeconds = seconds.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');
    const formattedHours = hours.toString().padStart(2, '0');

    if (days > 0) {
      return `${days} ${
        days === 1 ? 'day' : 'days'
      } ${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }

    if (hours > 0) {
      return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }

    return `${formattedMinutes}:${formattedSeconds}`;
  }

  if (days === 0 && hours === 0 && minutes === 0 && seconds > 0) {
    return `${seconds} sec ${
      documentType === 'videoLesson' ? 'video' : getTypeString(documentType)
    }`;
  }

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  }

  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hr' : 'hr'}`);
  }

  if (minutes > 0) {
    parts.push(`${minutes} min`);
  }

  if (parts.length === 0) {
    return undefined;
  }

  return `${parts.join(' ')} ${getTypeString(documentType)}`;
};

export const checkIsFavorite = (
  videoLessonId: number,
  favoritesMap: FavoritesMap,
) => {
  return favoritesMap.some((fav) => fav.documentId === videoLessonId);
};

export const mapDataToVideoCardProps = (
  lesson: VideoLesson,
  options?: {
    isFavorite?: boolean;
    onClickFavorite?: () => void;
    onClickLink?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  },
): VideoCardProps => {
  const {
    id,
    attributes: { title, slug, image },
  } = lesson;

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const target = e.target as HTMLElement;
    const isFavoriteClick = target.closest('button') !== null;

    if (!isFavoriteClick && options?.onClickLink) {
      options.onClickLink(e);
    }
  };

  return {
    link: {
      href:
        slug && slug !== SLUG_DEFAULT
          ? `/lessons/videos/${id}-${slug}`
          : `/lessons/videos/${makeSlug(id, title)}`,
      onClick: options?.onClickLink ? handleLinkClick : undefined,
    },
    title: {
      text: title,
    },
    variant: 'technique',
    cloudinaryId: image?.data?.attributes?.provider_metadata?.public_id,
    isFavorite: options?.isFavorite,
    onClickFavorite: options?.onClickFavorite,
  };
};
